import { useState } from "react";
import { Trash } from "@phosphor-icons/react";
import ModalCloseButton from "../../../../components/ui/ModalCloseButton";
import DatePicker from "../../../../components/ui/DatePicker";
import "../NivTasksPage.css";

/**
 * Full task editor modal: title, description, option pickers, list, due date.
 *
 * @param {Object}   task
 * @param {Object}   config   - board config (options + lists)
 * @param {function} onSave   - (taskId, data) => void
 * @param {function} onDelete - (task) => void
 * @param {function} onClose
 */
function TaskModal({ task, config, onSave, onDelete, onClose }) {
  const [title, setTitle] = useState(task.title || "");
  const [description, setDescription] = useState(task.description || "");
  const [status, setStatus] = useState(task.status || null);
  const [priority, setPriority] = useState(task.priority || null);
  const [type, setType] = useState(task.type || null);
  const [listId, setListId] = useState(task.listId || config?.lists?.[0]?.id || "");
  const [dueDate, setDueDate] = useState(
    task.dueDate ? String(task.dueDate).slice(0, 10) : ""
  );

  const handleSave = () => {
    const trimmed = title.trim();
    if (!trimmed) return;
    onSave(task.id, {
      title: trimmed,
      description: description.trim() || null,
      status,
      priority,
      type,
      listId,
      dueDate: dueDate || null,
    });
    onClose();
  };

  const renderOptionPicker = (label, field, value, setValue, allowClear) => {
    const options = config?.options?.[field] || [];
    return (
      <div className="nivtasks-modal-field">
        <label>{label}</label>
        <div className="nivtasks-modal-options">
          {options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={`nivtasks-pill nivtasks-modal-pill ${opt.id === value ? "is-selected" : ""}`}
              style={{ backgroundColor: opt.color }}
              onClick={() => setValue(allowClear && opt.id === value ? null : opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="nivtasks-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="nivtasks-modal" dir="ltr">
        <ModalCloseButton onClick={onClose} />
        <h3 className="nivtasks-modal-title">Edit Task</h3>

        <div className="nivtasks-modal-field">
          <label>Title</label>
          <input
            className="nivtasks-modal-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Task title"
          />
        </div>

        <div className="nivtasks-modal-field">
          <label>Description</label>
          <textarea
            className="nivtasks-modal-textarea"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add a description…"
          />
        </div>

        {renderOptionPicker("Status", "status", status, setStatus, false)}
        {renderOptionPicker("Priority", "priority", priority, setPriority, false)}
        {renderOptionPicker("Type", "type", type, setType, true)}

        <div className="nivtasks-modal-row">
          <div className="nivtasks-modal-field">
            <label>List</label>
            <select
              className="nivtasks-modal-select"
              value={listId}
              onChange={(e) => setListId(e.target.value)}
            >
              {(config?.lists || []).map((list) => (
                <option key={list.id} value={list.id}>
                  {list.name}
                </option>
              ))}
            </select>
          </div>
          <div className="nivtasks-modal-field">
            <label>Due date</label>
            <DatePicker value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>
        </div>

        <div className="nivtasks-modal-actions">
          <button
            type="button"
            className="nivtasks-btn nivtasks-btn-danger-ghost"
            onClick={() => {
              onClose();
              onDelete(task);
            }}
          >
            <Trash size={16} /> Delete
          </button>
          <div className="nivtasks-modal-actions-main">
            <button type="button" className="nivtasks-btn nivtasks-btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="nivtasks-btn nivtasks-btn-primary"
              onClick={handleSave}
              disabled={!title.trim()}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TaskModal;
